import { useEffect, useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ChevronDown } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import type { CategoryKey } from '@worldcoo/shared/core';
import { PartnerLogo } from '../partner-logo';
import { CATEGORY_ICONS } from './category-icons';
import { NgoPagination } from './ngo-pagination';
import type { PartnerNgo } from './use-partner-ngos';

/** Off the hand-off: five rows of seven on desktop, three rows of two per "Ver más" on mobile. */
const DESKTOP_PAGE = 35;
const MOBILE_PAGE = 6;
/** Closed, mobile shows no logos at all. */
const DESKTOP_CLOSED = 7;

/** A LINK, never an `onClick`: the sheet is a URL, so middle-click, copy-link and new tab all work. */
function NgoLogo(props: { ngo: PartnerNgo; selected: boolean; small: boolean }) {
	const { t } = useLingui();
	// lingui disallows a property access inside t``.
	const name = props.ngo.name;
	return (
		<Link
			to="/www/partner-ngos/$ngoId"
			params={{ ngoId: props.ngo.id }}
			// The sheet is a drawer over this page, so opening it must not scroll the directory to the top.
			resetScroll={false}
			aria-label={t`See the campaigns of ${name}`}
			className={cn(
				'group flex h-16 items-center justify-center rounded-2xl lg:h-[79px] lg:rounded-[16.7px]',
				// Full colour throughout: dimming them read as a filter making them fainter.
				'focus-visible:ring-ink cursor-pointer transition-colors p-1 hover:bg-[#efefef] focus-visible:ring-2 focus-visible:outline-none',
				props.selected && 'bg-[#efefef]',
			)}
		>
			{props.ngo.logo ? (
				<PartnerLogo
					src={props.ngo.logo.location}
					alt={props.ngo.name}
					// A step up from the merchant strips, as NGO artwork is our smallest; 128px is a 7-column cell.
					size={props.small ? 'md' : 'lg'}
				/>
			) : (
				<span className="text-ink/60 line-clamp-2 text-center text-sm font-medium">{props.ngo.name}</span>
			)}
		</Link>
	);
}

/**
 * `useIsMobile`, not CSS, as how many partners to render is a data decision. It resolves to desktop on
 * the server and during hydration, so a crawler still gets rendered logos.
 */
export function NgoCategoryRow(props: {
	category: CategoryKey;
	label: string;
	ngos: PartnerNgo[];
	selectedNgoId?: string;
	/** Set when a category filter has left this row alone on the page. */
	openByDefault?: boolean;
}) {
	const { t } = useLingui();
	const isMobile = useIsMobile(1024);
	const [open, setOpen] = useState(props.openByDefault ?? false);
	const [page, setPage] = useState(0);
	const [shown, setShown] = useState(MOBILE_PAGE);
	const gridId = useId();
	const Icon = CATEGORY_ICONS[props.category];

	// Filtering can shrink a category under the visitor's page, stranding them on an empty one.
	useEffect(() => {
		setPage(0);
		setShown(MOBILE_PAGE);
	}, [props.ngos.length]);

	// Follows the filter, not only the initial state; a manual toggle wins until the filter next changes.
	const openByDefault = props.openByDefault ?? false;
	useEffect(() => setOpen(openByDefault), [openByDefault]);

	const pageCount = Math.ceil(props.ngos.length / DESKTOP_PAGE);
	const visible = !open
		? isMobile
			? []
			: props.ngos.slice(0, DESKTOP_CLOSED)
		: isMobile
			? props.ngos.slice(0, shown)
			: props.ngos.slice(page * DESKTOP_PAGE, (page + 1) * DESKTOP_PAGE);

	const canToggle = isMobile || props.ngos.length > DESKTOP_CLOSED;
	const canShowMore = isMobile && open && shown < props.ngos.length;

	// The whole bar, as a 16px chevron is a poor tap target on a phone.
	const header = (
		<>
			<span className="flex min-w-0 items-center gap-2">
				<span className="bg-ink grid size-8 shrink-0 place-items-center rounded-lg text-white">
					<Icon className="size-4" />
				</span>
				<span className="text-base leading-[1.3] font-medium lg:text-xl">{props.label}</span>
			</span>

			{canToggle && (
				<span className="text-ink flex shrink-0 items-center gap-2 text-[15px] font-medium tracking-[-0.02em]">
					{/* On mobile it would crowd "Educación e Inserción laboral" at 343px. */}
					<span className="sr-only lg:not-sr-only">{open ? t`See less` : t`See more`}</span>
					<ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
				</span>
			)}
		</>
	);

	return (
		<section className="rounded-2xl bg-white p-4 lg:rounded-3xl lg:p-8">
			<h3>
				{canToggle ? (
					<button
						type="button"
						onClick={() => setOpen((isOpen) => !isOpen)}
						aria-expanded={open}
						aria-controls={gridId}
						className="flex w-full cursor-pointer items-center justify-between gap-4 text-left hover:opacity-70"
					>
						{header}
					</button>
				) : (
					/* Nothing to open, so the bar must not look or behave like a control. */
					<span className="flex w-full items-center justify-between gap-4">{header}</span>
				)}
			</h3>

			{visible.length > 0 && (
				<div id={gridId} className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:mt-6 lg:grid-cols-7 lg:gap-6">
					{visible.map((ngo) => (
						<NgoLogo key={ngo.id} ngo={ngo} selected={ngo.id === props.selectedNgoId} small={isMobile} />
					))}
				</div>
			)}

			{canShowMore && (
				<button
					type="button"
					onClick={() => setShown((count) => count + MOBILE_PAGE)}
					className="text-ink mt-6 flex cursor-pointer items-center gap-2 text-base font-medium tracking-[-0.02em] hover:opacity-70"
				>
					{t`See more`}
					<ChevronDown className="size-4" />
				</button>
			)}

			{open && !isMobile && <NgoPagination page={page} pageCount={pageCount} onChange={setPage} />}
		</section>
	);
}
